import {
  Alert,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { IoIosArrowBack } from "react-icons/io";
import { ReportRepository } from "../repositories/ReportRepository";
import { pdfjs } from "react-pdf";
import PdfViewer from "./PdfViewer";
import { MdOutlineDone } from "react-icons/md";
import SignAppEntry from "./SignAppEntry";
import { AppEntryDocRepository } from "../repositories/AppEntryDocRepository";
import InfoAlert from "../common/InfoAlert";
import { AppEntryRepository } from "../repositories/AppEntryRepository";
import { GoDotFill } from "react-icons/go";
import { useDispatch } from "react-redux";
import {
  notifyShowErrorMessage,
  notifyShowSuccessMessage,
} from "../../store/AlertSlice";
import { ErrorMessageResolver } from "../common/ErrorMessageResolver";
import ConfirmationDialog from "../common/ConfirmationDialog";

pdfjs.GlobalWorkerOptions.workerSrc = "/pdfjs/pdf.worker.min.mjs";

const steps = [
  "Пополнување на барање",
  "Потпишување на генерирани барања",
  "Испраќање до надлежни институции",
];

export default function EntryRequestsStepper({
  appEntry,
  setAppEntry,
  isGenerating,
  onStartGenerating,
  onCancelGenerating,
}) {
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const [completed, setCompleted] = useState({});
  const [formData, setFormData] = useState({
    attachmentsProvided: ["Сообраќаен проект за", "Извештај од ревизија за"],
    reason: "",
    locationDescription: "",
    projectTechnicalNumber: "",
    projectTitle: "",
    projectBureauName: "",
    creator: "Мирјана Младеновска",
    reviewer: "Марјан Копевски",
    approver: "Марјан Копевски",
    minister: "Александар Николоски",
  });
  const [docs, setDocs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [openPdfDialog, setOpenPdfDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState();
  const dispatch = useDispatch();

  useEffect(() => {
    if (activeStep === 1) {
      getGeneratedDocs();
    }
  }, [activeStep]);

  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    if (appEntry?.id) {
      AppEntryDocRepository.getGeneratedDocs(appEntry.id)
        .then((res) => {
          setDocs(res.data);
          if (res.data && res.data.length > 0) {
            setActiveStep(1);
            setCompleted((prev) => ({ ...prev, 0: true }));
          }
        })
        .catch((err) => {
          console.log(err);
        });

      setFormData({
        ...formData,
        locationDescription: appEntry?.appEntryData?.locationDescription || "",
        projectBureauName: appEntry?.appEntryData?.projectBureauName || "",
        projectTechnicalNumber:
          appEntry?.appEntryData?.projectTechnicalNumber || "",
        projectTitle: appEntry?.appEntryData?.projectTitle || "",
      });
    }
  }, [appEntry?.id]);

  const totalSteps = () => steps.length;
  const completedSteps = () => Object.keys(completed).length;
  const isLastStep = () => activeStep === totalSteps() - 1;
  const allStepsCompleted = () => completedSteps() === totalSteps();

  const handleNext = () => {
    if (activeStep === 1 && docs.length < 2) {
      setCompleted((prev) => ({}));
      dispatch(
        notifyShowErrorMessage(
          "Потребно е да генерерирате и потпишете го барањето пред да продолжите.",
        ),
      );
      return;
    }

    const newActiveStep =
      isLastStep() && !allStepsCompleted()
        ? steps.findIndex((step, i) => !(i in completed))
        : activeStep + 1;
    setActiveStep(newActiveStep);
  };

  const handleStep = (step) => () => {
    setActiveStep(step);
  };

  const handleComplete = () => {
    setCompleted({
      ...completed,
      [activeStep]: true,
    });
    handleNext();
    forwardToAuthorities();
  };

  const generateReport = () => {
    if (!formData.reason.trim()) return;
    setLoading(true);
    setDocs([]);
    ReportRepository.generateReport({
      street: formData?.street,
      reason: formData?.reason,
      creator: formData?.creator,
      reviewer: formData?.reviewer,
      approver: formData?.approver,
      minister: formData?.minister,
      appEntryId: appEntry?.id,
      appEntryType: appEntry?.type,
    })
      .then((res) => {
        setLoading(false);
        getGeneratedDocs();
        setActiveStep(1);
        setCompleted((prev) => ({ ...prev, 0: true }));
      })
      .catch((err) => {
        setLoading(false);
        console.log(err);
      });
  };

  const getGeneratedDocs = () => {
    AppEntryDocRepository.getGeneratedDocs(appEntry?.id)
      .then((res) => {
        setDocs(res.data);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const forwardToAuthorities = () => {
    setSaving(true);
    setLoading(true);
    AppEntryRepository.forwardToAuthorities(appEntry?.id)
      .then((res) => {
        setAppEntry(res.data);
        onCancelGenerating();
        setLoading(false);
        setSaving(false);
        dispatch(
          notifyShowSuccessMessage(
            "Барањата се успешно испратени до надлежните институции.",
          ),
        );
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
        dispatch(notifyShowErrorMessage(ErrorMessageResolver.resolve(err)));
        setSaving(false);
      });
  };

  return (
    <React.Fragment>
      <Grid
        item
        xs={12}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "15px",
          justifyContent: "space-between",
          marginTop: "10px",
        }}
      >
        {isGenerating && (
          <Button
            variant="transparent"
            color="primary"
            size="large"
            disableRipple
            disableTouchRipple
            onClick={() => onCancelGenerating()}
            startIcon={
              <IoIosArrowBack size={15} color={theme.palette.secondary.main} />
            }
          >
            <Typography
              variant="caption"
              sx={{
                fontSize: "14px",
                fontFamily: "Inter-Regular",
                marginLeft: "2px",
                color: theme.palette.secondary.main,
              }}
            >
              Назад
            </Typography>
          </Button>
        )}
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            padding: "10px 0",
          }}
        >
          {!isGenerating && (
            <>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "2px",
                }}
              >
                <GoDotFill
                  size={20}
                  color={activeStep !== 0 ? "#46b035" : "#d90429"}
                />
                <Typography variant="caption" style={{ fontSize: "12px" }}>
                  {activeStep !== 0
                    ? "Следен чекор:"
                    : "Процесот не е започнат"}
                </Typography>
              </div>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={() => onStartGenerating()}
              >
                <Typography>
                  {activeStep === 0
                    ? "Започни процес на генерирање"
                    : activeStep === 1
                      ? steps[activeStep]
                      : activeStep === 2
                        ? steps[activeStep]
                        : null}
                </Typography>
              </Button>
            </>
          )}
        </Box>
      </Grid>
      {isGenerating && (
        <React.Fragment>
          <Grid
            item
            xs={12}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "15px",
              justifyContent: isGenerating ? "space-between" : "flex-end",
              marginTop: "20px",
              padding: "10px",
            }}
          >
            <Box sx={{ width: "100%" }}>
              <Stepper nonLinear activeStep={activeStep}>
                {steps?.map((label, index) => (
                  <Step
                    key={label}
                    completed={completed[index]}
                    style={{
                      padding: "0 15px",
                    }}
                  >
                    <div
                      onClick={handleStep(index)}
                      style={{
                        padding: 0,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        justifyContent: "center",
                      }}
                    >
                      <StepLabel
                        StepIconComponent={() => null}
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                          justifyContent: "center",
                        }}
                      >
                        <div
                          style={{
                            color: "#fff",
                            backgroundColor:
                              activeStep === index
                                ? theme.palette.primary.main
                                : completed[index]
                                  ? theme.palette.success.main
                                  : "#e0e0e0",
                            borderRadius: "50%",
                            width: 24,
                            height: 24,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "0.8rem",
                          }}
                        >
                          {completed[index] && (
                            <MdOutlineDone sx={{ fontSize: 16 }} />
                          )}
                        </div>
                      </StepLabel>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        justifyContent: "center",
                        marginTop: "5px",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          whiteSpace: "nowrap",
                          color:
                            activeStep === index
                              ? theme.palette.secondary.main
                              : "inherit",
                        }}
                      >
                        Чекор {index + 1}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          fontFamily:
                            activeStep === index
                              ? "Inter-Bold"
                              : "Inter-Medium",
                          marginTop: "-3px",
                          width: "200px",
                          lineHeight: 1.2,
                        }}
                      >
                        {label}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          whiteSpace: "nowrap",
                          fontFamily:
                            activeStep === index
                              ? "Inter-Bold"
                              : "Inter-Medium",
                          marginTop: "-3px",
                          color:
                            activeStep === index
                              ? theme.palette.primary.main
                              : completed[index]
                                ? theme.palette.success.main
                                : "#b3b3b3",
                        }}
                      >
                        {completed[index]
                          ? "Завршено"
                          : activeStep === index
                            ? "Активно"
                            : "На чекање"}
                      </Typography>
                    </div>
                  </Step>
                ))}
              </Stepper>
              <div>
                {!allStepsCompleted() && (
                  <React.Fragment>
                    {activeStep === 0 ? (
                      <Grid container spacing={2} sx={{ padding: "20px" }}>
                        <Grid item xs={12} md={12}>
                          <TextField
                            label="Барање"
                            placeholder="Барање"
                            autoComplete="off"
                            value={formData.reason || ""}
                            multiline
                            rows={3}
                            onChange={(event) => {
                              setFormData({
                                ...formData,
                                reason: event.target.value,
                              });
                            }}
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} md={12}>
                          <TextField
                            label="Проект"
                            autoComplete="off"
                            disabled
                            placeholder={`Во ова поле внесете Наслов на проектот, пример: 'ОСНОВЕН СООБРАЌАЕН ПРОЕКТ ЗА ВРЕМЕН РЕЖИМ НА СООБРАЌАЈ ЗА ВОВЕДУВАЊЕ НА ДЕВИЈАЦИJА НА ДЕЛНИЦА...'`}
                            value={formData.projectTitle || ""}
                            multiline
                            rows={3}
                            onChange={(event) => {
                              setFormData({
                                ...formData,
                                projectTitle: event.target.value,
                              });
                            }}
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Локација"
                            placeholder="Локација"
                            disabled
                            autoComplete="off"
                            value={formData.locationDescription || ""}
                            onChange={(event) => {
                              setFormData({
                                ...formData,
                                locationDescription: event.target.value,
                              });
                            }}
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} md={6}></Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Проектантско биро"
                            placeholder="Проектантско биро"
                            autoComplete="off"
                            disabled
                            value={formData.projectBureauName || ""}
                            onChange={(event) => {
                              setFormData({
                                ...formData,
                                projectBureauName: event.target.value,
                              });
                            }}
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} md={6}></Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Технички број"
                            placeholder="Технички број"
                            autoComplete="off"
                            disabled
                            value={formData.projectTechnicalNumber || ""}
                            onChange={(event) => {
                              setFormData({
                                ...formData,
                                projectTechnicalNumber: event.target.value,
                              });
                            }}
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} md={6}></Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Изготвил"
                            placeholder="Изготвил"
                            autoComplete="off"
                            value={formData.creator || ""}
                            onChange={(event) => {
                              setFormData({
                                ...formData,
                                creator: event.target.value,
                              });
                            }}
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Проверил"
                            placeholder="Проверил"
                            autoComplete="off"
                            value={formData.reviewer || ""}
                            onChange={(event) => {
                              setFormData({
                                ...formData,
                                reviewer: event.target.value,
                              });
                            }}
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Одобрил"
                            placeholder="Одобрил"
                            autoComplete="off"
                            value={formData.approver || ""}
                            onChange={(event) => {
                              setFormData({
                                ...formData,
                                approver: event.target.value,
                              });
                            }}
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Министер"
                            placeholder="Министер"
                            autoComplete="off"
                            value={formData.minister || ""}
                            onChange={(event) => {
                              setFormData({
                                ...formData,
                                approver: event.target.value,
                              });
                            }}
                            fullWidth
                          />
                        </Grid>
                      </Grid>
                    ) : (
                      ""
                    )}
                    {activeStep === 1 && (
                      <SignAppEntry
                        appEntry={appEntry}
                        docs={docs}
                        setDocs={setDocs}
                        setPdfBlob={setPdfBlob}
                        setOpenPdfDialog={setOpenPdfDialog}
                      />
                    )}
                    {activeStep === 2 && (
                      <Grid item xs={12} style={{ marginTop: "20px" }}>
                        <Grid container spacing={0} style={{ padding: "10px" }}>
                          <Grid
                            item
                            xs={12}
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "5px",
                              padding: "10px",
                            }}
                          >
                            <InfoAlert
                              message={
                                "Доколку сите барања за согласност се потпишани, барањата ќе бидат проследени до надлежните институции."
                              }
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                    )}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        pt: 2,
                        justifyContent: "center",
                        alignItems: "center",
                        gap: 2,
                        flexWrap: "wrap",
                      }}
                    >
                      {activeStep === 0 && (
                        <Button
                          variant="contained"
                          color="primary"
                          size="large"
                          fullWidth
                          onClick={generateReport}
                          disabled={!formData.reason || loading}
                          sx={{ width: 250 }}
                        >
                          {loading ? (
                            <CircularProgress
                              size={20}
                              id="circular-progress"
                            />
                          ) : (
                            <Typography className="login-button-text">
                              Генерирај
                            </Typography>
                          )}
                        </Button>
                      )}
                      {activeStep === 1 && (
                        <>
                          <Button
                            variant="outlined"
                            color="secondary"
                            size="large"
                            onClick={() => setActiveStep(0)}
                            sx={{ width: 250 }}
                          >
                            <Typography>Повторно генерирање</Typography>
                          </Button>
                          <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            disabled={loading}
                            onClick={() => {
                              setCompleted((prev) => ({ ...prev, 1: true }));
                              handleNext();
                            }}
                            sx={{ width: 250 }}
                          >
                            {loading ? (
                              <CircularProgress
                                size={20}
                                id="circular-progress"
                              />
                            ) : (
                              <Typography className="login-button-text">
                                Следно
                              </Typography>
                            )}
                          </Button>
                        </>
                      )}
                      {activeStep === 2 && (
                        <>
                          {docs.length >= 2 && (
                            <Button
                              variant="contained"
                              color="primary"
                              size="large"
                              onClick={() =>
                                setOpenConfirmationDialog({
                                  title: "Потврда",
                                  message:
                                    "Дали сте сигурни дека сакате да го испратите барањето до надлежните институции?",
                                  onConfirm: () => {
                                    setOpenConfirmationDialog();
                                    handleComplete();
                                  },
                                })
                              }
                              disabled={loading}
                            >
                              <Typography className="login-button-text">
                                Испрати до надлежни институции
                              </Typography>
                            </Button>
                          )}
                        </>
                      )}
                    </Box>
                  </React.Fragment>
                )}
              </div>
            </Box>
          </Grid>
        </React.Fragment>
      )}
      <Dialog
        open={openPdfDialog}
        onClose={() => {
          setOpenPdfDialog(false);
          handleClose();
        }}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Барање за согласност
        </DialogTitle>
        <DialogContent>
          {pdfBlob && <PdfViewer pdfBlob={pdfBlob} />}
        </DialogContent>
        <DialogActions style={{ padding: "10px 25px" }}>
          <Button
            variant="contained"
            colors="primary"
            size="large"
            onClick={() => {
              setOpenPdfDialog(false);
              handleClose();
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: 80,
              }}
            >
              <Typography>Затвори</Typography>
            </Box>
          </Button>
        </DialogActions>
      </Dialog>
      {saving && (
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={true}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
      {openConfirmationDialog && (
        <ConfirmationDialog
          open={Boolean(openConfirmationDialog)}
          title={openConfirmationDialog.title}
          message={openConfirmationDialog.message}
          onCancel={() => setOpenConfirmationDialog()}
          onConfirm={openConfirmationDialog.onConfirm}
        />
      )}
    </React.Fragment>
  );
}
